import React, { useState, useLayoutEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Sidebar from '../components/Sidebar';
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { TutorialProvider, useTutorial } from '../contexts/TutorialContext';
import TutorialModule from '../components/TutorialModule';
import AdminTutorialModule from '../components/AdminTutorialModule';
import ExplorerTree from '../components/ExplorerTree';
import ContentViewingModal from '../components/ContentViewingModal';
import { UserProfile } from '../components/UserProfile';
import { useCustomAuth } from "../contexts/AuthContext";
import { ANNOTATION_SESSION_ENDPOINT, AWS_LAMBDA_ENDPOINT } from "../utils/constants";
import NeuroMuseLogo from "../assets/img/logo/NeuroMuseLogo.svg";
import { authFetch } from "../utils/authFetch";
import { normalizeUserAnnotatorAssignments } from "../utils/annotatorAssignment";

const TutorialContent = ({ handleLogout }) => {
  const [expanded, setExpanded] = useState([]);
  const [readyToInteract, setReadyToInteract] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const { setUserData, isAdminMode } = useCustomAuth();
  
  // Admin mode states
  const [adminTab, setAdminTab] = useState('editing'); // 'editing' or 'groups'
  const [selectedTargetType, setSelectedTargetType] = useState('group'); // 'user' or 'group'
  const [selectedTarget, setSelectedTarget] = useState(''); // user ID or group name
  const [allUsers, setAllUsers] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [targetRequiredMap, setTargetRequiredMap] = useState({});
  const [originalRequiredMap, setOriginalRequiredMap] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [applyingChanges, setApplyingChanges] = useState(false);
  
  // Dialog states
  const [createGroupDialog, setCreateGroupDialog] = useState(false);
  const [deleteGroupDialog, setDeleteGroupDialog] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupError, setNewGroupError] = useState('');
  
  // Groups panel collapse state
  const [groupsPanelExpanded, setGroupsPanelExpanded] = useState(false);
  
  // Tutorial context
  const { 
    tutorialStructure, 
    loading, 
    dataReady,
    error,
    clearError,
    isModuleLocked,
    selectContent,
    setError,
    loadTutorialStructure
  } = useTutorial();
  
  // Get user name from auth profile
  const userName = auth.user?.profile?.name || "User";
  
  // Load admin data when in admin mode
  const loadAdminData = async () => {
    if (!isAdminMode) return;
    
    setLoadingAdmin(true);
    try {
      // Load users and groups in parallel
      const [usersResponse, groupsResponse] = await Promise.all([
        authFetch(`${AWS_LAMBDA_ENDPOINT}/listAllUsers`).then(res => res.json()),
        authFetch(`${AWS_LAMBDA_ENDPOINT}/listAllGroups`).then(res => res.json())
      ]);
      
      setAllUsers(usersResponse.users || []);
      setAllGroups(groupsResponse.groups || []);
      
      // Only load required map when in groups tab and a target is selected
      if (adminTab === 'groups' && selectedTarget && selectedTarget !== '') {
        await loadTargetRequiredMap(selectedTargetType, selectedTarget);
      }
      
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load admin data');
    } finally {
      setLoadingAdmin(false);
    }
  };
  
  // Load required map for specific target
  const loadTargetRequiredMap = async (targetType, targetId) => {
    try {
      let settingId;
      if (targetType === 'group') {
        settingId = targetId === 'default' ? 'default' : `group-${targetId}`;
      } else {
        settingId = `user-${targetId}`;
      }
      
      const response = await authFetch(`${AWS_LAMBDA_ENDPOINT}/getTutorialSettingBySettingId?settingId=${encodeURIComponent(settingId)}`);
      
      if (!response.ok) {
        console.error(`API call failed: ${response.status} ${response.statusText}`);
        setTargetRequiredMap({});
        return;
      }
      
      const data = await response.json();
      const requiredMap = data.requiredMap || {};
      setTargetRequiredMap(requiredMap);
      setOriginalRequiredMap(requiredMap);
      setPendingChanges({});
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Error loading target required map:', err);
      setTargetRequiredMap({});
      setOriginalRequiredMap({});
      setPendingChanges({});
      setHasUnsavedChanges(false);
    }
  };
  
  // Update required status for an item (local only, not saved until apply)
  const updateRequiredStatus = (itemId, required) => {
    const updatedRequiredMap = {
      ...targetRequiredMap,
      [itemId]: required
    };
    
    const newPendingChanges = {
      ...pendingChanges,
      [itemId]: required
    };
    
    setTargetRequiredMap(updatedRequiredMap);
    setPendingChanges(newPendingChanges);
    
    // Check if there are unsaved changes
    const hasChanges = Object.keys(newPendingChanges).some(id => 
      newPendingChanges[id] !== originalRequiredMap[id]
    );
    setHasUnsavedChanges(hasChanges);
  };

  // Apply pending changes
  const applyChanges = async () => {
    if (!hasUnsavedChanges) return;

    setApplyingChanges(true);
    try {
      let settingId;
      if (selectedTargetType === 'group') {
        settingId = selectedTarget === 'default' ? 'default' : `group-${selectedTarget}`;
      } else {
        settingId = `user-${selectedTarget}`;
      }
      
      // Update tutorial setting
      await authFetch(`${AWS_LAMBDA_ENDPOINT}/updateTutorialSetting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settingId,
          tutorialId: 'main',
          requiredMap: targetRequiredMap
        })
      });

      // Update user content status for all affected users based on the current setting being modified
      const affectedUsers = [];
      
      if (selectedTargetType === 'group') {
        if (selectedTarget === 'default') {
          // Default setting affects users with settingMode 'default' or no settingMode
          affectedUsers.push(...allUsers.filter(user => 
            (user.settingMode === 'default' || !user.settingMode)
          ));
        } else {
          // Specific group setting affects users with this exact group
          affectedUsers.push(...allUsers.filter(user => 
            user.settingMode === 'group' && user.groupId === selectedTarget
          ));
        }
      } else if (selectedTargetType === 'user') {
        // User-specific setting affects only this user (and only if they're in customized mode)
        const user = allUsers.find(u => u.userId === selectedTarget);
        if (user && user.settingMode === 'customized') {
          affectedUsers.push(user);
        }
      }

      // console.log(`Found ${affectedUsers.length} users affected by changes to ${settingId}:`, 
      //   affectedUsers.map(u => `${u.userId} (${u.settingMode || 'default'}${u.groupId ? ` - ${u.groupId}` : ''})`));

      // Update content status for all affected users in batch
      if (affectedUsers.length > 0) {
        try {
          const userIds = affectedUsers.map(user => user.userId);
          const response = await authFetch(`${AWS_LAMBDA_ENDPOINT}/resolveRequiredStatusForUser`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userIds: userIds
            })
          });
          
          if (!response.ok) {
            throw new Error(`Failed to resolve users: ${response.status}`);
          }
          
          const batchResult = await response.json();
          // console.log(`Batch resolve results: ${batchResult.processed} successful, ${batchResult.errors} failed`);
          
          if (batchResult.errors > 0) {
            console.warn('Failed to resolve some users:', batchResult.errors);
          }
        } catch (err) {
          console.error('Error in batch resolve operation:', err);
          // Fallback to individual calls if batch fails
          // console.log('Falling back to individual resolve calls...');
          
          const resolveResults = await Promise.allSettled(affectedUsers.map(async (user) => {
            try {
              const response = await authFetch(`${AWS_LAMBDA_ENDPOINT}/resolveRequiredStatusForUser`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  userId: user.userId
                })
              });
              
              if (!response.ok) {
                throw new Error(`Failed to resolve user ${user.userId}: ${response.status}`);
              }
              
              return { userId: user.userId, success: true };
            } catch (err) {
              console.error(`Error updating user ${user.userId} content status:`, err);
              return { userId: user.userId, success: false, error: err.message };
            }
          }));

          // Log results
          const successful = resolveResults.filter(r => r.status === 'fulfilled' && r.value.success);
          const failed = resolveResults.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success));
          
          // console.log(`Fallback resolve results: ${successful.length} successful, ${failed.length} failed`);
          if (failed.length > 0) {
            console.warn('Failed to resolve users:', failed.map(f => 
              f.status === 'rejected' ? f.reason : f.value.error
            ));
          }
        }
      }

      // Reset tracking states
      setOriginalRequiredMap(targetRequiredMap);
      setPendingChanges({});
      setHasUnsavedChanges(false);
      
      // console.log(`Applied changes to ${affectedUsers.length} users`);
    } catch (err) {
      console.error('Error applying changes:', err);
      setError('Failed to apply changes');
    } finally {
      setApplyingChanges(false);
    }
  };
  
  React.useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.profile?.email) return;
    const email = auth.user.profile.email;
    authFetch(
      `${ANNOTATION_SESSION_ENDPOINT}/getUser?user_id=${encodeURIComponent(email)}`,
      {},
      auth.user?.id_token
    )
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch user data");
        return res.json();
      })
      .then(data => {
        setUserData(normalizeUserAnnotatorAssignments(data));
      })
      .catch(err => console.error("Error refreshing user data:", err));
  }, [auth.isAuthenticated, auth.user, setUserData]);
  
  // Load admin data when entering admin mode
  React.useEffect(() => {
    if (isAdminMode) {
      loadAdminData();
    }
  }, [isAdminMode]);
  
  // Load required map when switching to groups tab
  React.useEffect(() => {
    if (isAdminMode && adminTab === 'groups' && allUsers.length > 0 && allGroups.length > 0) {
      loadTargetRequiredMap('group', 'default');
    }
  }, [adminTab, allUsers, allGroups]);
  
  // Handle target selection change
  const handleTargetChange = async (newTargetType, newTarget) => {
    // Handle special create new group option
    if (newTarget === '__create_new__') {
      setCreateGroupDialog(true);
      return;
    }
    
    setSelectedTargetType(newTargetType);
    setSelectedTarget(newTarget);
    await loadTargetRequiredMap(newTargetType, newTarget);
  };

  // Handle user setting mode change
  const handleUserSettingModeChange = async (userId, newSettingMode, groupId = null) => {
    try {
      const requestData = {
        userId,
        settingMode: newSettingMode,
        groupId: newSettingMode === 'group' ? groupId : null
      };
      
      // console.log('Updating user setting mode:', requestData);
      
      const response = await authFetch(`${AWS_LAMBDA_ENDPOINT}/updateUserSettingMode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('API Error:', response.status, errorData);
        
        // If group setting not found, try to create it automatically
        if (response.status === 404 && errorData.error === 'Group setting not found' && newSettingMode === 'group' && groupId) {
          // console.log('Group setting not found, attempting to create it automatically...');
          try {
            const createResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/createTutorialSetting`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                settingId: `group-${groupId}`,
                tutorialId: 'main',
                requiredMap: {},
                metadata: {
                  groupName: groupId,
                  createdAt: new Date().toISOString(),
                  autoCreated: true
                }
              })
            });
            
            if (createResponse.ok) {
              // console.log('Group setting created successfully, retrying user update...');
              // Retry the original request
              const retryResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/updateUserSettingMode`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
              });
              
              if (retryResponse.ok) {
                // console.log('User setting mode updated successfully after creating group setting');
                // Continue with the normal flow
                await loadAdminData();
                if (selectedTargetType === 'user' && selectedTarget === userId) {
                  await loadTargetRequiredMap('user', userId);
                }
                return; // Exit successfully
              }
            }
          } catch (createError) {
            console.error('Failed to auto-create group setting:', createError);
          }
        }
        
        throw new Error(errorData.error || `Failed to update user setting mode (${response.status})`);
      }

      // Refresh users list
      await loadAdminData();
      
      // If we're currently viewing this user and they switched to customized mode,
      // we need to reload their settings
      if (selectedTargetType === 'user' && selectedTarget === userId) {
        await loadTargetRequiredMap('user', userId);
      }
    } catch (err) {
      console.error('Error updating user setting mode:', err);
      setError('Failed to update user setting mode');
    }
  };

  // Handle group deletion
  const handleDeleteGroup = (groupName) => {
    setGroupToDelete(groupName);
    setDeleteGroupDialog(true);
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete) return;

    try {
      const response = await authFetch(`${AWS_LAMBDA_ENDPOINT}/deleteTutorialSetting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settingId: `group-${groupToDelete}`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete group');
      }

      // Refresh data and reset selection if needed
      await loadAdminData();
      if (selectedTarget === groupToDelete) {
        setSelectedTarget('');
        setTargetRequiredMap({});
        setOriginalRequiredMap({});
        setPendingChanges({});
        setHasUnsavedChanges(false);
      }

      setDeleteGroupDialog(false);
      setGroupToDelete(null);
    } catch (err) {
      console.error('Error deleting group:', err);
      setError('Failed to delete group');
    }
  };

  // Handle group creation
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      setNewGroupError('Group name is required');
      return;
    }

    // Check if group name already exists
    if (allGroups.some(group => group.groupName.toLowerCase() === newGroupName.trim().toLowerCase() || group.groupId.toLowerCase() === newGroupName.trim().toLowerCase())) {
      setNewGroupError('Group name already exists');
      return;
    }

    try {
      const response = await authFetch(`${AWS_LAMBDA_ENDPOINT}/createTutorialSetting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settingId: `group-${newGroupName.trim()}`,
          tutorialId: 'main',
          requiredMap: {},
          metadata: {
            groupName: newGroupName.trim(),
            createdAt: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create group');
      }

      // Refresh data and select the new group
      await loadAdminData();
      setSelectedTarget(newGroupName.trim());
      await loadTargetRequiredMap('group', newGroupName.trim());

      setCreateGroupDialog(false);
      setNewGroupName('');
      setNewGroupError('');
    } catch (err) {
      console.error('Error creating group:', err);
      setNewGroupError('Failed to create group');
    }
  };

  // Handle structure updates (create, delete, upload)
  const handleStructureUpdate = async (action, item) => {
    try {
      switch (action) {
        case 'create':
          // Create new section/module
          const response = await authFetch(`${AWS_LAMBDA_ENDPOINT}/uploadTutorialContent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              parentId: item.parentId,
              title: item.title,
              description: '', // Send empty description for modules
              type: 'module'
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
          }
          
          // Refresh tutorial structure after successful creation
          await loadTutorialStructure();
          break;
          
        case 'delete':
          // Delete item
          const deleteResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/deleteTutorialItem`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              itemId: item.itemId
            })
          });
          
          if (!deleteResponse.ok) {
            const errorData = await deleteResponse.json();
            throw new Error(errorData.error || `HTTP error! status: ${deleteResponse.status}`);
          }

          // If deleting content, remove it from all users and tutorial settings
          if (item.type === 'content') {
            try {
              const removeContentResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/removeContentFromAllSettings`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  itemId: item.itemId
                })
              });

              if (!removeContentResponse.ok) {
                console.warn('Failed to remove content from all settings, but content was deleted');
              } else {
                const removeContentData = await removeContentResponse.json();
                // console.log(`Removed content from ${removeContentData.updatedUsers} users and ${removeContentData.updatedSettings} settings`);
              }
            } catch (removeContentError) {
              console.warn('Error removing content from all settings:', removeContentError);
              // Don't throw error here, content was still deleted successfully
            }
          }
          
          // Refresh tutorial structure after successful deletion
          await loadTutorialStructure();
          break;
          
        case 'upload':
          // Upload content to S3 and save to DynamoDB
          // console.log('Upload content:', item);
          
          const file = item.file;
          const baseFileName = `${Date.now()}_${file.name}`;
          
          // Map frontend content types to actual MIME types and determine folder
          let actualContentType;
          let folder = '';
          switch (item.contentType) {
            case 'video':
              actualContentType = file.type || 'video/mp4';
              folder = 'videos';
              break;
            case 'pdf':
              actualContentType = 'application/pdf';
              folder = 'pdfs';
              break;
            case 'text':
              actualContentType = 'text/markdown';
              folder = 'texts';
              break;
            default:
              actualContentType = file.type || 'application/octet-stream';
          }
          
          // Create full S3 key with folder path
          const s3Key = folder ? `${folder}/${baseFileName}` : baseFileName;
          
          // Get presigned URL for upload
          const presignedResponse = await authFetch(
            `${AWS_LAMBDA_ENDPOINT}/getPresignedURL?s3Key=${encodeURIComponent(s3Key)}&contentType=${encodeURIComponent(actualContentType)}&operation=put`
          );
          
          if (!presignedResponse.ok) {
            throw new Error('Failed to get upload URL');
          }
          
          const { presignedUrl } = await presignedResponse.json();
          
          // Upload file to S3 (use regular fetch, not authFetch, for presigned URLs)
          const uploadResponse = await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': actualContentType
            }
          });
          
          if (!uploadResponse.ok) {
            throw new Error('Failed to upload file to S3');
          }
          
          // Save content info to DynamoDB
          const saveResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/uploadTutorialContent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              parentId: item.parentId,
              title: item.title,
              description: item.description || '',
              type: 'content',
              contentType: actualContentType, // Send full MIME type for folder assignment
              s3Key: s3Key
            })
          });
          
          if (!saveResponse.ok) {
            const errorData = await saveResponse.json();
            throw new Error(errorData.error || `HTTP error! status: ${saveResponse.status}`);
          }

          const saveData = await saveResponse.json();
          const newItemId = saveData.itemId;

          // Add the new content to all users and tutorial settings
          try {
            const addContentResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/addContentToAllSettings`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                itemId: newItemId,
                defaultRequired: false // New content is optional by default
              })
            });

            if (!addContentResponse.ok) {
              console.warn('Failed to add content to all settings, but content was created');
            } else {
              const addContentData = await addContentResponse.json();
              // console.log(`Added content to ${addContentData.updatedUsers} users and ${addContentData.updatedSettings} settings`);
            }
          } catch (addContentError) {
            console.warn('Error adding content to all settings:', addContentError);
            // Don't throw error here, content was still created successfully
          }
          
          // Refresh tutorial structure after successful upload
          await loadTutorialStructure();
          break;

        case 'edit':
          // Update item metadata
          const updatePayload = {
            itemId: item.itemId,
            title: item.title
          };
          
          // Only include description for content items (modules don't have descriptions)
          if (item.type === 'content' && item.description !== undefined) {
            updatePayload.description = item.description;
          }
          
          // console.log('Sending update payload:', JSON.stringify(updatePayload, null, 2));
          // console.log('Item data received:', JSON.stringify(item, null, 2));
          
          const updateResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/updateTutorialItemMetadata`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatePayload)
          });
          
          if (!updateResponse.ok) {
            const errorData = await updateResponse.json();
            throw new Error(errorData.error || `HTTP error! status: ${updateResponse.status}`);
          }
          
          // Refresh tutorial structure after successful update
          await loadTutorialStructure();
          break;

        case 'reupload':
          // Re-upload content file to S3 and update S3 key in DynamoDB
          // console.log('Re-upload content:', item);
          
          const reuploadFile = item.file;
          const reuploadBaseFileName = `${Date.now()}_${reuploadFile.name}`;
          
          // Map frontend content types to actual MIME types and determine folder
          let reuploadActualContentType;
          let reuploadFolder = '';
          switch (item.contentType) {
            case 'video':
              reuploadActualContentType = reuploadFile.type || 'video/mp4';
              reuploadFolder = 'videos';
              break;
            case 'pdf':
              reuploadActualContentType = 'application/pdf';
              reuploadFolder = 'pdfs';
              break;
            case 'text':
              reuploadActualContentType = 'text/markdown';
              reuploadFolder = 'texts';
              break;
            default:
              reuploadActualContentType = reuploadFile.type || 'application/octet-stream';
          }
          
          // Create full S3 key with folder path
          const reuploadS3Key = reuploadFolder ? `${reuploadFolder}/${reuploadBaseFileName}` : reuploadBaseFileName;
          
          // Get presigned URL for upload
          const reuploadPresignedResponse = await authFetch(
            `${AWS_LAMBDA_ENDPOINT}/getPresignedURL?s3Key=${encodeURIComponent(reuploadS3Key)}&contentType=${encodeURIComponent(reuploadActualContentType)}&operation=put`
          );
          
          if (!reuploadPresignedResponse.ok) {
            throw new Error('Failed to get upload URL for re-upload');
          }
          
          const { presignedUrl: reuploadPresignedUrl } = await reuploadPresignedResponse.json();
          
          // Upload new file to S3 (use regular fetch, not authFetch, for presigned URLs)
          const reuploadResponse = await fetch(reuploadPresignedUrl, {
            method: 'PUT',
            body: reuploadFile,
            headers: {
              'Content-Type': reuploadActualContentType
            }
          });
          
          if (!reuploadResponse.ok) {
            throw new Error('Failed to upload new file to S3');
          }
          
          // Update content info in DynamoDB with new S3 key
          const reuploadUpdatePayload = {
            itemId: item.itemId,
            s3Key: reuploadS3Key
          };
          
          const reuploadUpdateResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/updateTutorialItemMetadata`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(reuploadUpdatePayload)
          });
          
          if (!reuploadUpdateResponse.ok) {
            const errorData = await reuploadUpdateResponse.json();
            throw new Error(errorData.error || `HTTP error! status: ${reuploadUpdateResponse.status}`);
          }
          
          // Refresh tutorial structure after successful re-upload
          await loadTutorialStructure();
          break;

        case 'reorder':
          // Reorder tutorial items
          // console.log('Reorder item:', item);
          
          const reorderResponse = await authFetch(`${AWS_LAMBDA_ENDPOINT}/updateTutorialItemPosition`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              itemId: item.itemId,
              newParentId: item.newParentId,
              newOrder: item.newOrder
            })
          });
          
          if (!reorderResponse.ok) {
            const errorData = await reorderResponse.json();
            throw new Error(errorData.error || `HTTP error! status: ${reorderResponse.status}`);
          }
          
          // Refresh tutorial structure after successful reorder
          await loadTutorialStructure();
          break;
          
        default:
          throw new Error('Unknown action');
      }
      
      // Refresh tutorial structure after any changes
      // This would be handled by the TutorialContext
      
    } catch (err) {
      console.error('Structure update failed:', err);
      setError('Failed to update structure');
      throw err;
    }
  };
  
  // Set ready to interact after data is ready and a small delay
  React.useEffect(() => {
    if (dataReady) {
      const timer = setTimeout(() => {
        setReadyToInteract(true);
      }, 150); // Slightly longer delay for main page
      
      return () => clearTimeout(timer);
    } else {
      setReadyToInteract(false);
    }
  }, [dataReady]);
  
  // Auto-collapse locked modules
  React.useEffect(() => {
    if (dataReady && tutorialStructure.length > 0) {
      setExpanded(prev => prev.filter(moduleId => {
        const module = tutorialStructure.find(m => m.itemId === moduleId);
        return module && !isModuleLocked(module, isAdminMode);
      }));
    }
  }, [dataReady, tutorialStructure, isModuleLocked]);

  useLayoutEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      html {
        overflow-y: scroll;
      }
      
      :root {
        overflow-y: auto;
        overflow-x: hidden;
      }
      
      :root body {
        position: absolute;
      }
      
      body {
        width: 100vw;
        overflow: hidden;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleChange = (panel) => (event, isExpanded) => {
    // Only allow changes when ready to interact
    if (!readyToInteract) return;
    
    // Find the module to check if it's locked
    const module = tutorialStructure.find(m => m.itemId === panel);
    
    if (isExpanded) {
      // Don't allow expanding locked modules
      if (module && isModuleLocked(module, isAdminMode)) {
        return;
      }
      // Add panel to expanded array if not already present
      setExpanded(prev => prev.includes(panel) ? prev : [...prev, panel]);
    } else {
      // Remove panel from expanded array
      setExpanded(prev => prev.filter(item => item !== panel));
    }
  };

  // Treat modules with parentId === 'root' as top-level modules for the new data structure
  const topLevelModules = tutorialStructure.filter(item => 
    item.type === 'module' && (!item.parentId || item.parentId === 'root')
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Sidebar />
        <Box sx={{ marginLeft: '200px', flexGrow: 1 }}>
          {/* Top Navigation Bar */}
          <Box sx={{ 
            position: 'fixed',
            top: 0,
            left: '200px',
            right: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            display: 'flex', 
            alignItems: 'center', 
            py: 2, 
            px: 3, 
            borderBottom: '1px solid #eaeaea'
          }}>
            {/* Logo (NeuroMuse AI) - Left */}
            <Box sx={{ flexBasis: '250px' }}>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <img
                  src={NeuroMuseLogo}
                  alt="NeuroMuse Logo"
                  style={{ height: "40px", cursor: "pointer" }}
                  onClick={() => navigate('/')}
                />
              </Box>
            </Box>
            
            {/* Empty Center Section */}
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              justifyContent: 'center'
            }}>
            </Box>
            
            {/* User Profile - Right */}
            <UserProfile handleLogout={handleLogout} userName={userName} />
          </Box>

          {/* Loading Content */}
          <Container sx={{ py: 4, pt: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Loading Tutorial Content...
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box sx={{ marginLeft: '200px', flexGrow: 1 }}>
                  {/* Top Navigation Bar */}
          <Box sx={{ 
            position: 'fixed',
            top: 0,
            left: '200px',
            right: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            display: 'flex', 
            alignItems: 'center', 
            py: 2, 
            px: 3, 
            borderBottom: '1px solid #eaeaea'
          }}>
            {/* Logo (NeuroMuse AI) - Left */}
            <Box sx={{ flexBasis: '250px' }}>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <img
                  src={NeuroMuseLogo}
                  alt="NeuroMuse Logo"
                  style={{ height: "40px", cursor: "pointer" }}
                  onClick={() => navigate('/')}
                />
              </Box>
            </Box>
            
            {/* Empty Center Section */}
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              justifyContent: 'center'
            }}>
            </Box>
            
            {/* User Profile - Right */}
            <UserProfile handleLogout={handleLogout} userName={userName} />
          </Box>

        {/* Main Content */}
        <Container sx={{ 
          py: 4,
          pt: 12,
          pl: 0,
          pr: 3,
          transition: 'padding-left 0.3s ease'
        }} maxWidth={false}>
          {/* Page Title and Admin Controls */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 4,
            marginLeft: isAdminMode && adminTab === 'editing' ? '304px' : '24px',
            transition: 'margin-left 0.3s ease'
          }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontFamily: 'Arial, sans-serif',
              fontWeight: 700,
              color: '#333'
            }}
          >
            Tutorial
          </Typography>

            {/* Center - Groups Selector (always when in groups mode) */}
            {isAdminMode && adminTab === 'groups' && (
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  {/* Container for the whole component */}
                  <Box sx={{
                    backgroundColor: 'white',
                    border: '1px solid #000',
                    borderRadius: groupsPanelExpanded ? '16px 16px 0 0' : '16px',
                    boxShadow: 'none',
                    overflow: groupsPanelExpanded ? 'visible' : 'hidden',
                    width: 300,
                    position: 'relative',
                    zIndex: 1000
                  }}>
                    {/* Always visible top button */}
                    <Button
                      onClick={() => setGroupsPanelExpanded(!groupsPanelExpanded)}
                      sx={{
                        width: '100%',
                        minHeight: 48,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textTransform: 'none',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: groupsPanelExpanded ? '16px 16px 0 0' : '16px',
                        color: '#333',
                        px: 2,
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '&:focus': {
                          outline: 'none',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {(() => {
                          if (selectedTargetType === 'group') {
                            if (!selectedTarget) {
                              return (
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: '#999',
                                    lineHeight: 1.2
                                  }}
                                >
                                  Group: Select a Group
                                </Typography>
                              );
                            } else if (selectedTarget === 'default') {
                              return (
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: '#333',
                                    lineHeight: 1.2
                                  }}
                                >
                                  Group: Default
                                </Typography>
                              );
                            } else {
                              // Find group name for the selected group ID
                              const group = allGroups.find(g => g.groupId === selectedTarget);
                              const groupName = group ? group.groupName : selectedTarget;
                              return (
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: '#333',
                                    lineHeight: 1.2
                                  }}
                                >
                                  Group: {groupName}
                                </Typography>
                              );
                            }
                          } else {
                            // selectedTargetType === 'user'
                            return (
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontFamily: 'Arial, sans-serif',
                                  fontSize: '1rem',
                                  fontWeight: 700,
                                  color: selectedTarget ? '#333' : '#999',
                                  lineHeight: 1.2
                                }}
                              >
                                User: {selectedTarget || 'Select a User'}
                              </Typography>
                            );
                          }
                        })()}
                      </Box>
                      <ExpandMoreIcon 
                        sx={{ 
                          color: '#666',
                          transform: groupsPanelExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                          position: 'absolute',
                          right: 16
                        }}
                      />
                    </Button>

                    {/* Expanded content panel */}
                    {groupsPanelExpanded && (
                      <Box sx={{ 
                        position: 'absolute',
                        top: 'calc(100% - 1px)',
                        left: '-1px',
                        right: '-1px',
                        backgroundColor: 'white',
                        border: '1px solid #000',
                        borderTop: 'none',
                        borderRadius: '0 0 16px 16px',
                        boxShadow: 'none',
                        p: 3,
                        maxHeight: 'calc(100vh - 200px)',
                        overflow: 'auto',
                        zIndex: 1001
                      }}>
                      {/* Target Selection Buttons */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedTargetType('group');
                            // Don't auto-select any group when switching to group mode
                            if (selectedTargetType !== 'group') {
                              setSelectedTarget('');
                              setTargetRequiredMap({});
                              setOriginalRequiredMap({});
                              setPendingChanges({});
                              setHasUnsavedChanges(false);
                            }
                          }}
                          sx={{ 
                            minWidth: 70, 
                            fontSize: '0.75rem',
                            backgroundColor: selectedTargetType === 'group' ? '#424242' : 'transparent',
                            color: selectedTargetType === 'group' ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '8px',
                            '&:hover': {
                              backgroundColor: selectedTargetType === 'group' ? '#333' : 'rgba(102, 102, 102, 0.1)',
                            }
                          }}
                        >
                          Group
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedTargetType('user');
                            // Don't auto-select any user when switching to user mode
                            if (selectedTargetType !== 'user') {
                              setSelectedTarget('');
                              setTargetRequiredMap({});
                              setOriginalRequiredMap({});
                              setPendingChanges({});
                              setHasUnsavedChanges(false);
                            }
                          }}
                          sx={{ 
                            minWidth: 70, 
                            fontSize: '0.75rem',
                            backgroundColor: selectedTargetType === 'user' ? '#424242' : 'transparent',
                            color: selectedTargetType === 'user' ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '8px',
                            '&:hover': {
                              backgroundColor: selectedTargetType === 'user' ? '#333' : 'rgba(102, 102, 102, 0.1)',
                            }
                          }}
                        >
                          User
                        </Button>
                      </Box>

                      {/* Target Selection Dropdown */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      {selectedTargetType === 'group' ? (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <Select
                            value={selectedTarget}
                            onChange={(e) => handleTargetChange('group', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              <Typography sx={{ color: 'text.secondary' }}>Select a Group...</Typography>
                            </MenuItem>
                            <MenuItem value="default">
                              <Typography>Default</Typography>
                            </MenuItem>
                            {allGroups.filter(group => group.groupName.toLowerCase() !== 'default').map((group) => (
                              <MenuItem key={group.groupId} value={group.groupId}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                  <Typography>{group.groupName}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ({group.userCount} users)
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                            <MenuItem value="__create_new__">
                              <Typography sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                                + Create New Group...
                              </Typography>
                            </MenuItem>
                          </Select>
                        </FormControl>
                                              ) : (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <Select
                            value={selectedTarget}
                            onChange={(e) => handleTargetChange('user', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              <Typography sx={{ color: 'text.secondary' }}>Select a User...</Typography>
                            </MenuItem>
                            {allUsers.map((user) => {
                              // Function to get display text for user setting
                              const getUserSettingDisplay = (user) => {
                                const settingMode = user.settingMode || 'default';
                                if (settingMode === 'group' && user.groupId) {
                                  // Find the group name for this groupId
                                  const group = allGroups.find(g => g.groupId === user.groupId);
                                  return group ? group.groupName : user.groupId;
                                }
                                return settingMode;
                              };

                              return (
                                <MenuItem key={user.userId} value={user.userId}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <Typography>{user.userId}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ({getUserSettingDisplay(user)})
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      )}
                      </Box>



                      {/* User Settings Management */}
                      {selectedTargetType === 'user' && selectedTarget && selectedTarget !== '' && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                          {(() => {
                            const user = allUsers.find(u => u.userId === selectedTarget);
                            return user ? (
                              <Box>
                                <Typography variant="body2" sx={{ mb: 1, fontSize: '12px' }}>
                                  Current Mode: <strong>{user.settingMode || 'default'}</strong>
                                  {user.settingMode === 'group' && user.groupId && (() => {
                                    const group = allGroups.find(g => g.groupId === user.groupId);
                                    const groupName = group ? group.groupName : user.groupId;
                                    return ` (Group: ${groupName})`;
                                  })()}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, justifyContent: 'center' }}>
                                  <Button
                                    size="small"
                                    onClick={() => handleUserSettingModeChange(user.userId, 'default')}
                                    sx={{ 
                                      fontSize: '11px', 
                                      minWidth: 'auto', 
                                      px: 1,
                                      backgroundColor: user.settingMode === 'default' ? '#424242' : 'transparent',
                                      color: user.settingMode === 'default' ? 'white' : '#666',
                                      border: 'none',
                                      borderRadius: '8px',
                                      '&:hover': {
                                        backgroundColor: user.settingMode === 'default' ? '#333' : 'rgba(102, 102, 102, 0.1)',
                                      }
                                    }}
                                  >
                                    Default
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => handleUserSettingModeChange(user.userId, 'customized')}
                                    sx={{ 
                                      fontSize: '11px', 
                                      minWidth: 'auto', 
                                      px: 1,
                                      backgroundColor: user.settingMode === 'customized' ? '#424242' : 'transparent',
                                      color: user.settingMode === 'customized' ? 'white' : '#666',
                                      border: 'none',
                                      borderRadius: '8px',
                                      '&:hover': {
                                        backgroundColor: user.settingMode === 'customized' ? '#333' : 'rgba(102, 102, 102, 0.1)',
                                      }
                                    }}
                                  >
                                    Customized
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      // If not in group mode, set to group mode with first available non-default group
                                      if (user.settingMode !== 'group') {
                                        const availableGroups = allGroups.filter(group => group.groupName.toLowerCase() !== 'default');
                                        // console.log('Available groups:', availableGroups);
                                        const firstGroup = availableGroups.length > 0 ? availableGroups[0].groupId : null;
                                        // console.log('Selected first group:', firstGroup);
                                        if (firstGroup) {
                                          handleUserSettingModeChange(user.userId, 'group', firstGroup);
                                        } else {
                                          console.error('No available groups found');
                                          setError('No groups available. Please create a group first.');
                                        }
                                      }
                                    }}
                                    sx={{ 
                                      fontSize: '11px', 
                                      minWidth: 'auto', 
                                      px: 1,
                                      backgroundColor: user.settingMode === 'group' ? '#424242' : 'transparent',
                                      color: user.settingMode === 'group' ? 'white' : '#666',
                                      border: 'none',
                                      borderRadius: '8px',
                                      '&:hover': {
                                        backgroundColor: user.settingMode === 'group' ? '#333' : 'rgba(102, 102, 102, 0.1)',
                                      }
                                    }}
                                  >
                                    Group
                                  </Button>
                                </Box>
                                
                                {/* Group selection dropdown - only show when user is in group mode */}
                                {user.settingMode === 'group' && (
                                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ mb: 1, fontSize: '12px' }}>
                                      Select Group:
                                    </Typography>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                      <Select
                                        value={user.groupId || ''}
                                        onChange={(e) => handleUserSettingModeChange(user.userId, 'group', e.target.value)}
                                      >
                                        {allGroups.filter(group => group.groupName.toLowerCase() !== 'default').map((group) => (
                                          <MenuItem key={group.groupId} value={group.groupId}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                              <Typography>{group.groupName}</Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                ({group.userCount} users)
                                              </Typography>
                                            </Box>
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                )}
                              </Box>
                            ) : null;
                          })()}
                        </Box>
                      )}

                      {/* Group Management */}
                      {selectedTargetType === 'group' && selectedTarget && selectedTarget !== '' && selectedTarget !== 'default' && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteGroup(selectedTarget)}
                            disabled={loadingAdmin}
                            sx={{ fontSize: '11px' }}
                          >
                            Delete Group: {selectedTarget}
                          </Button>
                        </Box>
                      )}

                      {loadingAdmin && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </Box>
                  )}
                  </Box>
                </Box>
                {loadingAdmin && !groupsPanelExpanded && <CircularProgress size={20} />}
              </Box>
            )}
            
            {/* Admin Mode Controls */}
            {isAdminMode && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Apply Button - only show in groups mode when there are unsaved changes */}
                {adminTab === 'groups' && hasUnsavedChanges && (
                  <Button
                    onClick={applyChanges}
                    disabled={applyingChanges}
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      backgroundColor: '#4caf50',
                      '&:hover': {
                        backgroundColor: '#45a049',
                      },
                      '&:disabled': {
                        backgroundColor: '#cccccc',
                      }
                    }}
                  >
                    {applyingChanges ? 'Applying...' : 'Apply'}
                  </Button>
                )}
                
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    onClick={() => setAdminTab('editing')}
                    sx={{
                      backgroundColor: adminTab === 'editing' ? '#424242' : 'transparent',
                      color: adminTab === 'editing' ? 'white' : '#666',
                      border: 'none',
                      borderRadius: '25px',
                      minHeight: '36px',
                      px: 3,
                      py: 1,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: adminTab === 'editing' ? '#333' : 'rgba(102, 102, 102, 0.1)',
                      }
                    }}
                  >
                    Editing Portal
                  </Button>
                  <Button
                    onClick={() => setAdminTab('groups')}
                    sx={{
                      backgroundColor: adminTab === 'groups' ? '#424242' : 'transparent',
                      color: adminTab === 'groups' ? 'white' : '#666',
                      border: 'none',
                      borderRadius: '25px',
                      minHeight: '36px',
                      px: 3,
                      py: 1,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: adminTab === 'groups' ? '#333' : 'rgba(102, 102, 102, 0.1)',
                      }
                    }}
                  >
                    Groups
                  </Button>
                </Box>
              </Box>
            )}
          </Box>



          {/* Error Display */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={clearError}
            >
              {error}
            </Alert>
          )}

          {/* Tutorial Modules */}
          {dataReady && (topLevelModules.length > 0 || (isAdminMode && adminTab === 'editing')) ? (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Left Sidebar - Explorer (only in admin editing mode) */}
              {isAdminMode && adminTab === 'editing' && (
                <Box sx={{ width: 0, flexShrink: 0 }}>
                  <Paper sx={{ 
                    p: 1.5, 
                    position: 'fixed', 
                    top: 76, 
                    left: 200, 
                    bottom: 0, 
                    width: '280px',
                    backgroundColor: '#2c2c2c', 
                    color: 'white',
                    borderRadius: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 100
                  }}>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                      <ExplorerTree 
                        tutorialStructure={tutorialStructure}
                        onContentSelect={selectContent}
                        onStructureUpdate={handleStructureUpdate}
                      />
                    </Box>
                  </Paper>
                </Box>
              )}
              
              {/* Main Content Area */}
              <Box sx={{ 
                flex: 1, 
                marginLeft: isAdminMode && adminTab === 'editing' ? '280px' : '24px',
                transition: 'margin-left 0.3s ease'
              }}>
                {/* Show tutorial modules for editing required/optional settings */}
                {isAdminMode && adminTab === 'groups' && (
                  (selectedTargetType === 'group' && selectedTarget && selectedTarget !== '') || 
                  (selectedTargetType === 'user' && selectedTarget && selectedTarget !== '' &&
                   allUsers.find(u => u.userId === selectedTarget)?.settingMode === 'customized')
                ) ? (
            topLevelModules.map((module) => (
                    <Box key={module.itemId} mb={2}>
                      <AdminTutorialModule
                        module={module}
                        expanded={expanded.includes(module.itemId)}
                        onToggle={handleChange(module.itemId)}
                        targetRequiredMap={targetRequiredMap}
                        onRequiredChange={updateRequiredStatus}
                        isEditingMode={false}
                      />
                    </Box>
                  ))
                ) : isAdminMode && adminTab === 'groups' && selectedTargetType === 'user' ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: 'calc(100vh - 300px)'
                  }}>
                    <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        Choose Settings Mode
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          const user = allUsers.find(u => u.userId === selectedTarget);
                          if (user?.settingMode === 'group') {
                            const group = allGroups.find(g => g.groupId === user.groupId);
                            const groupName = group ? group.groupName : user.groupId;
                            return `This user is currently in the "${groupName}" group. To modify settings, please select "Group" to change the group's settings, or set the user's mode to "Customized" to create individual settings for this user.`;
                          } else if (user?.settingMode === 'default') {
                            return `This user is currently using default settings. To modify settings, please select "Group" to change the default settings, or set the user's mode to "Customized" to create individual settings for this user.`;
                          } else {
                            return `To edit required/optional settings for a user, please set their setting mode to "Customized" first.`;
                          }
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                ) : isAdminMode && adminTab === 'groups' && selectedTargetType === 'group' && (!selectedTarget || selectedTarget === '') ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: 'calc(100vh - 300px)'
                  }}>
                    <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        Select a Group
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please select a group from the dropdown above to edit its required/optional content settings.
                      </Typography>
                    </Box>
                  </Box>
                ) : isAdminMode && adminTab === 'editing' ? (
                  topLevelModules.length > 0 ? (
                    topLevelModules.map((module) => (
                      <Box key={module.itemId} mb={2}>
                        <AdminTutorialModule
                          module={module}
                          expanded={expanded.includes(module.itemId)}
                          onToggle={handleChange(module.itemId)}
                          targetRequiredMap={{}}
                          onRequiredChange={() => {}}
                          isEditingMode={true}
                        />
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        No tutorial modules yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create your first tutorial section using the Explorer panel on the left.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', opacity: 0.8 }}>
                        💡 Tip: Click the "+" button next to "NeuroMuse Tutorial" to get started
                      </Typography>
                    </Box>
                  )
                ) : (
                  topLevelModules.map((module) => (
                    <Box key={module.itemId} mb={2}>
                      <TutorialModule
                        module={module}
                        expanded={expanded.includes(module.itemId)}
                        onToggle={handleChange(module.itemId)}
                      />
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          ) : dataReady ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No tutorial content available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tutorial content will appear here once it's loaded.
              </Typography>
            </Box>
          ) : null}
        </Container>
      </Box>

      {/* Content Viewing Modal */}
      <ContentViewingModal />

      {/* Create Group Dialog */}
      <Dialog open={createGroupDialog} onClose={() => setCreateGroupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a name for the new group. This group will have its own required/optional content settings.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            variant="outlined"
            value={newGroupName}
            onChange={(e) => {
              setNewGroupName(e.target.value);
              setNewGroupError('');
            }}
            error={!!newGroupError}
            helperText={newGroupError}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateGroupDialog(false);
            setNewGroupName('');
            setNewGroupError('');
          }}>
            Cancel
          </Button>
          <Button onClick={handleCreateGroup} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Group Dialog */}
      <Dialog open={deleteGroupDialog} onClose={() => setDeleteGroupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the group "{groupToDelete}"?
          </DialogContentText>
          {groupToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="warning.main">
                ⚠️ Warning: Users currently using this group setting will be automatically switched to the Default group setting.
              </Typography>
              {allUsers.filter(user => user.settingMode === 'group' && user.groupId === groupToDelete).length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Affected users:
                  </Typography>
                  <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                    {allUsers
                      .filter(user => user.settingMode === 'group' && user.groupId === groupToDelete)
                      .map(user => (
                        <Typography component="li" key={user.userId} variant="body2" color="text.secondary">
                          {user.userId}
                        </Typography>
                      ))
                    }
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteGroupDialog(false);
            setGroupToDelete(null);
          }}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteGroup} variant="contained" color="error">
            Delete Group
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Tutorial = ({ handleLogout }) => {
  return (
    <TutorialProvider>
      <TutorialContent handleLogout={handleLogout} />
    </TutorialProvider>
  );
};

export default Tutorial;
